import { useState } from 'react'

import { F0Box, F0Button, F0Heading, F0OneIcon, F0Text } from '@factorialco/f0-react'
import { Cross } from '@factorialco/f0-react/icons/app'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { useOpenDashboardAction } from 'modules/ai/copilotActions/canvas/dashboards/useOpenDashboardAction'

// NOTE: `discover.mp4` is placed by the user at this path. Missing it will
// only fail at bundle time (vite); `tsc` is happy because `vite/client`
// types cover the import.
import discoverVideo from 'modules/ai_reports/pages/DiscoverPage/assets/discover.mp4'

// Scoped per (company, access) — a shared device with multiple operators or
// a user switching tenants should not inherit a dismissal from another scope.
const buildDismissedStorageKey = (companyId: string, accessId: string | undefined) =>
  `ai_reports.discover_banner.dismissed.${companyId}.${accessId ?? 'anon'}`

const readDismissed = (key: string) => {
  try {
    return window.localStorage.getItem(key) === '1'
  } catch {
    return false
  }
}

const DiscoverBanner = () => {
  const { access, company } = useCurrentSession()
  const dismissedStorageKey = buildDismissedStorageKey(company.id, access?.id)
  const { openChatForNewDashboard } = useOpenDashboardAction()
  const [dismissed, setDismissed] = useState(() => readDismissed(dismissedStorageKey))

  if (dismissed) return null

  const handleDismiss = () => {
    try {
      window.localStorage.setItem(dismissedStorageKey, '1')
    } catch {
      // Storage may be unavailable (private mode, quota); fall back to in-memory dismiss.
    }
    setDismissed(true)
  }

  return (
    <F0Box display="flex" flexDirection="column" padding="xl">
      <div style={{ position: 'relative' }}>
        <F0Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          gap="2xl"
          borderRadius="lg"
          border="default"
          borderColor="secondary"
        >
          <F0Box display="flex" flexDirection="column" gap="md" grow padding="2xl">
            <F0OneIcon size="lg" />
            <F0Heading
              variant="heading-large"
              content={i18n.t('ai_reports.discover.banner.title')}
            />
            <F0Text variant="body" content={i18n.t('ai_reports.discover.banner.description')} />
            <F0Box marginTop="lg">
              <F0Button
                variant="ai"
                label={i18n.t('ai_reports.dashboards.create_report')}
                onClick={openChatForNewDashboard}
              />
            </F0Box>
          </F0Box>
          <F0Box grow>
            {/* TODO: ~5MB asset bundled in git — migrate to CDN or re-encode (WebM)
                in a follow-up. preload="metadata" is a hint that's largely ignored
                with autoPlay, but kept for parity with non-autoplay paths. */}
            <video
              src={discoverVideo}
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              style={{ width: '100%', borderRadius: 12, display: 'block' }}
            />
          </F0Box>
        </F0Box>
        <div style={{ position: 'absolute', top: 12, right: 12 }}>
          <F0Button
            variant="ghost"
            icon={Cross}
            label={i18n.t('common.close')}
            hideLabel
            onClick={handleDismiss}
          />
        </div>
      </div>
    </F0Box>
  )
}

export default DiscoverBanner
